import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../supabase_client.jsx";
import "./notifications.css";

function safeName(u) {
  if (!u) return "Unknown";
  return (
    u.display_name ||
    u.full_name ||
    u.username ||
    u.name ||
    u.email ||
    (u.user_id ? String(u.user_id).slice(0, 8) : "Unknown")
  );
}

function toDateOnly(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export default function Notifications({ currentUser, onViewSelect }) {
  const userId = currentUser?.user?.id || null;

  const [loading, setLoading] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const lastSeenKey = useMemo(() => {
    if (!userId) return null;
    return `dwyg_notifications_last_seen_${userId}`;
  }, [userId]);

  useEffect(() => {
    if (!lastSeenKey) return;
    localStorage.setItem(lastSeenKey, new Date().toISOString());
  }, [lastSeenKey]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const { data: reqRows, error: reqErr } = await supabase
          .from("connection_requests")
          .select("id,requester_id,receiver_id,status,created_at")
          .eq("receiver_id", userId)
          .eq("status", "pending")
          .order("created_at", { ascending: false });

        if (reqErr) console.warn("Notifications: request load failed:", reqErr.message);

        const requesterIds = Array.from(
          new Set((reqRows || []).map((r) => r.requester_id).filter(Boolean))
        );

        let requesterProfiles = {};
        if (requesterIds.length > 0) {
          const { data: userRows, error: userErr } = await supabase
            .from("users").select("*").in("user_id", requesterIds);

          if (userErr) {
            const fallback = await supabase.from("users").select("*").in("id", requesterIds);
            if (!fallback.error) {
              (fallback.data || []).forEach((u) => { requesterProfiles[u.user_id || u.id] = u; });
            }
          } else {
            (userRows || []).forEach((u) => { requesterProfiles[u.user_id] = u; });
          }
        }

        const requestsHydrated = (reqRows || []).map((r) => ({
          ...r,
          requesterProfile: requesterProfiles[r.requester_id] || null,
        }));

        const [{ data: bmRows, error: bmErr }, { data: yesRows, error: yesErr }] =
          await Promise.all([
            supabase.from("bookmarked_events").select("event_id").eq("user_id", userId),
            supabase.from("marked_yes_events").select("event_id").eq("user_id", userId),
          ]);

        if (bmErr) console.warn("Notifications: bookmark load failed:", bmErr.message);
        if (yesErr) console.warn("Notifications: yes load failed:", yesErr.message);

        const bookmarkedSet = new Set((bmRows || []).map((r) => Number(r.event_id)));
        const yesSet = new Set((yesRows || []).map((r) => Number(r.event_id)));
        const allIds = Array.from(new Set([...bookmarkedSet, ...yesSet].filter((n) => Number.isFinite(n))));

        let upcomingEvents = [];
        if (allIds.length > 0) {
          const { data: evRows, error: evErr } = await supabase
            .from("events").select("*").in("id", allIds);

          if (evErr) {
            console.warn("Notifications: events load failed:", evErr.message);
          } else {
            const today = toDateOnly(new Date());
            upcomingEvents = (evRows || [])
              .filter((e) => {
                const d = e?.date ? toDateOnly(e.date) : null;
                return d && d >= today;
              })
              .sort((a, b) => new Date(a.date) - new Date(b.date))
              .map((e) => ({
                ...e,
                isBookmarked: bookmarkedSet.has(Number(e.id)),
                isGoing: yesSet.has(Number(e.id)),
              }));
          }
        }

        if (!cancelled) {
          setPendingRequests(requestsHydrated);
          setUpcoming(upcomingEvents);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [userId]);

  const totalCount = pendingRequests.length + upcoming.length;

  return (
    <div className="notifications-page-container">
      <div className="home-card">
        {/* App title banner */}
        <div className="app-title-banner">
          <h1 className="app-title-text">Do You Wanna Go?</h1>
        </div>

        {/* Nav */}
        <div className="calendar-top-nav">
          <div className="nav-left">
            <button className="nav-tab" onClick={() => onViewSelect("user")}>Calendar</button>
            <button className="nav-tab" onClick={() => onViewSelect("my-events")}>My Events</button>
            <button className="nav-tab" onClick={() => onViewSelect("connections")}>My Connections</button>
          </div>

          <div className="nav-right">
            <button type="button" className="nav-icon-circle nav-icon-button nav-notification-button" aria-label="Notifications" onClick={() => onViewSelect("notifications")}>
              <svg className="nav-bell-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8a6 6 0 10-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
                <path d="M13.73 21a2 2 0 01-3.46 0" />
              </svg>
            </button>

            <div className="nav-avatar-circle">
              <button className="nav-tab nav-profile-button" onClick={() => onViewSelect("user-profile")} aria-label="Profile">
                <svg className="nav-user-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Full-width content */}
        <div className="notifications-main">
          <h1 className="notifications-page-title">Notifications</h1>
          <p className="notifications-page-subtitle">
            {loading ? "Loading your notifications…" : `${totalCount} notification${totalCount !== 1 ? "s" : ""}`}
          </p>

          {loading && <div className="notifications-loading">Loading…</div>}

          {/* Connection requests */}
          {!loading && (
            <div className="notifications-section">
              <div className="notifications-section-title">Connection Requests</div>
              {pendingRequests.length === 0 ? (
                <div className="notifications-empty">No new connection requests right now.</div>
              ) : (
                pendingRequests.map((r) => (
                  <div key={r.id} className="notification-card">
                    <div className="notification-icon-circle request">👤</div>
                    <div className="notification-main">
                      <div className="notification-text">
                        <strong>{safeName(r.requesterProfile)}</strong> sent you a connection request.
                      </div>
                      <div className="notification-sub">
                        {r.created_at ? new Date(r.created_at).toLocaleString() : ""}
                      </div>
                    </div>
                    <button className="notification-action primary" onClick={() => onViewSelect("connections")}>
                      View request
                    </button>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Upcoming events — click opens popup */}
          {!loading && (
            <div className="notifications-section">
              <div className="notifications-section-title">Upcoming Events</div>
              {upcoming.length === 0 ? (
                <div className="notifications-empty">
                  No upcoming events from your bookmarks or "Yes" list.
                </div>
              ) : (
                upcoming.map((e) => (
                  <div
                    key={e.id}
                    className="notification-card notif-event-clickable"
                    onClick={() => setSelectedEvent(e)}
                  >
                    {e.image_url ? (
                      <img src={e.image_url} alt={e.name} className="notif-event-thumb" />
                    ) : (
                      <div className="notif-event-thumb-placeholder" />
                    )}
                    <div className="notification-main">
                      <div className="notification-text">
                        <strong>{e.name || "Event"}</strong>
                      </div>
                      <div className="notification-sub">
                        {e.date ? new Date(e.date).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : ""}
                        {e.isGoing && <span className="notification-badge going">Going</span>}
                        {e.isBookmarked && <span className="notification-badge bookmarked">Bookmarked</span>}
                      </div>
                    </div>
                    <span className="notif-event-arrow">›</span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* ===== Event popup ===== */}
      {selectedEvent && (
        <div className="notif-popup-overlay" onClick={() => setSelectedEvent(null)}>
          <div className="notif-popup" onClick={(e) => e.stopPropagation()}>
            {/* Hero image */}
            <div className="notif-popup-image-wrap">
              {selectedEvent.image_url ? (
                <img src={selectedEvent.image_url} alt={selectedEvent.name} className="notif-popup-image" />
              ) : (
                <div className="notif-popup-image-placeholder" />
              )}
              <button className="notif-popup-close" onClick={() => setSelectedEvent(null)} aria-label="Close">✕</button>
            </div>

            {/* Content */}
            <div className="notif-popup-body">
              <div className="notif-popup-header">
                <h2 className="notif-popup-title">{selectedEvent.name || "Event"}</h2>
                <div className="notif-popup-badges">
                  {selectedEvent.isGoing && <span className="notification-badge going">Going</span>}
                  {selectedEvent.isBookmarked && <span className="notification-badge bookmarked">Bookmarked</span>}
                </div>
              </div>

              <div className="notif-popup-divider" />

              <div className="notif-popup-fields">
                {selectedEvent.date && (
                  <div className="notif-popup-row">
                    <span className="notif-popup-label">Date</span>
                    <span className="notif-popup-value">
                      {new Date(selectedEvent.date).toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
                    </span>
                  </div>
                )}
                {(selectedEvent.start_time || selectedEvent.end_time) && (
                  <div className="notif-popup-row">
                    <span className="notif-popup-label">Time</span>
                    <span className="notif-popup-value">
                      {selectedEvent.start_time || "TBA"}{selectedEvent.end_time ? ` – ${selectedEvent.end_time}` : ""}
                    </span>
                  </div>
                )}
                {(selectedEvent.location || selectedEvent.address) && (
                  <div className="notif-popup-row">
                    <span className="notif-popup-label">Location</span>
                    <span className="notif-popup-value">{selectedEvent.location || selectedEvent.address}</span>
                  </div>
                )}
                {selectedEvent.description && (
                  <div className="notif-popup-row notif-popup-row-col">
                    <span className="notif-popup-label">Description</span>
                    <span className="notif-popup-value">{selectedEvent.description}</span>
                  </div>
                )}
              </div>

              <div className="notif-popup-footer">
                <button className="notification-action primary" onClick={() => { setSelectedEvent(null); onViewSelect("user"); }}>
                  View on calendar
                </button>
                <button className="notification-action secondary" onClick={() => setSelectedEvent(null)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}